import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { DollarSign, Activity, Lock, Unlock, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function UsageCostPage() {
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [u, o] = await Promise.all([superAdminApi.usage(), superAdminApi.aiOverview()]);
      setData(u.data);
      setOverview(o.data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveBudget = async (t) => {
    const v = edits[t.slug];
    if (v === undefined || v === '') { toast.message('Kein Wert eingegeben'); return; }
    setBusy('budget-' + t.slug);
    try { await superAdminApi.aiSetAllowance(t.slug, { ai_monthly_included_chf: Number(v) }); toast.success(`Budget für ${t.slug} gesetzt`); await load(); }
    catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  const toggleLock = async (t) => {
    setBusy('lock-' + t.slug);
    try { await superAdminApi.aiSetLock(t.slug, !t.manual_lock); toast.success(!t.manual_lock ? `${t.slug} gesperrt` : `${t.slug} entsperrt`); await load(); }
    catch { toast.error('Aktion fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  if (loading) return <div className="text-slate-600" data-testid="sa-usage-loading">Lädt…</div>;
  if (!data) return <div className="text-rose-600">Nutzungsdaten konnten nicht geladen werden.</div>;

  return (
    <div data-testid="sa-usage" className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Kosten & Nutzung</h1>
        <p className="mt-1 text-sm text-slate-600">KI-Verbrauch der Plattform, letzte {data.period_days} Tage.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-700"><DollarSign className="h-5 w-5" /></div>
          <div className="text-3xl font-semibold text-slate-900">${data.totals.cost_usd}</div>
          <div className="text-sm text-slate-600">Kosten gesamt</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-700"><Activity className="h-5 w-5" /></div>
          <div className="text-3xl font-semibold text-slate-900">{data.totals.calls}</div>
          <div className="text-sm text-slate-600">KI-Aufrufe</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500">Tokens</div>
          <div className="mt-2 text-sm text-slate-700">Ein: {data.totals.tokens_in.toLocaleString()}</div>
          <div className="text-sm text-slate-700">Aus: {data.totals.tokens_out.toLocaleString()}</div>
        </div>
      </div>

      {/* Pro Mandant (Phase 2) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-usage-per-tenant">
        <h2 className="mb-1 text-lg font-medium text-slate-900">Verbrauch & Budget pro Mandant</h2>
        <p className="mb-4 text-xs text-slate-500">Monatsverbrauch gegen enthaltenes Guthaben. Budget anpassen oder KI-Funktionen notfalls sperren.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="pb-3">Mandant</th><th className="pb-3 text-right">Verbr. / Inkl.</th><th className="pb-3 text-right">Anfragen</th><th className="pb-3 text-center">Status</th><th className="pb-3">Budget CHF</th><th className="pb-3 text-right">Sperre</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-200">
              {(overview?.tenants || []).map((t) => (
                <tr key={t.slug} data-testid={`sa-usage-tenant-${t.slug}`}>
                  <td className="py-3 text-slate-800">{t.name || t.slug}<div className="text-xs text-slate-500">{t.slug}</div></td>
                  <td className="py-3 text-right text-slate-700">{chf(t.consumed_chf)} / {chf(t.included_chf)}<div className="text-xs text-slate-500">{Math.round(t.pct_used || 0)}%</div></td>
                  <td className="py-3 text-right text-slate-600">{(t.requests || 0).toLocaleString()}</td>
                  <td className="py-3 text-center">
                    {t.manual_lock
                      ? <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-xs text-rose-700">Gesperrt</span>
                      : t.paused
                        ? <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-700">Pausiert</span>
                        : <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700">Aktiv</span>}
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-1">
                      <input type="number" placeholder={String(t.included_chf)} className="w-20 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800" value={edits[t.slug] ?? ''} onChange={(e) => setEdits({ ...edits, [t.slug]: e.target.value })} data-testid={`sa-usage-budget-${t.slug}`} />
                      <button onClick={() => saveBudget(t)} disabled={busy === 'budget-' + t.slug} className="rounded-lg bg-indigo-500/20 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-500/30" data-testid={`sa-usage-budget-save-${t.slug}`}>{busy === 'budget-' + t.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}</button>
                    </div>
                  </td>
                  <td className="py-3 text-right">
                    <button onClick={() => toggleLock(t)} disabled={busy === 'lock-' + t.slug} className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs ${t.manual_lock ? 'bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25' : 'bg-rose-500/15 text-rose-700 hover:bg-rose-500/25'}`} data-testid={`sa-usage-lock-${t.slug}`}>
                      {busy === 'lock-' + t.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (t.manual_lock ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />)}
                      {t.manual_lock ? 'Entsperren' : 'Sperren'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-medium text-slate-900">Verbrauch nach Funktion</h2>
        {data.by_service.length === 0 ? (
          <p className="text-sm text-slate-500">Keine Daten im Zeitraum.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="pb-3 font-medium">Funktion</th>
                  <th className="pb-3 text-right font-medium">Aufrufe</th>
                  <th className="pb-3 text-right font-medium">Tokens</th>
                  <th className="pb-3 text-right font-medium">Kosten</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {data.by_service.map((s) => (
                  <tr key={s.service} data-testid={`sa-usage-row-${s.service}`}>
                    <td className="py-3 text-slate-800">{s.service}</td>
                    <td className="py-3 text-right text-slate-700">{s.calls}</td>
                    <td className="py-3 text-right text-slate-600">{(s.tokens_in + s.tokens_out).toLocaleString()}</td>
                    <td className="py-3 text-right font-medium text-amber-700">${s.cost_usd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
